import { faPlus } from '@fortawesome/free-solid-svg-icons';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { ERouteStaticParams, Routes } from '../../classes/Routes';
import LocationsSearch from '../../components/Location/LocationSearch/LocationsSearch';
import Alert from '../../components/UI/Alert/Alert';
import Button from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import { IAppState, IAuthState, ILocationState } from '../../interfaces';
import { linkHandler } from '../../shared/utility';
import * as actions from '../../store/actions';
import { ETranslation } from '../../translations/translation-keys';

interface IProps extends RouteComponentProps {}

const Locations: React.FC<IProps> = ({ history }) => {
  const dispatch = useDispatch();

  const { loading, error, locations } = useSelector<IAppState, ILocationState>(state => state.locations);
  const { authUser } = useSelector<IAppState, IAuthState>(state => state.auth);
  const { sort, onSort, items: sortedLocations } = useTableSort({ data: locations });

  const { t } = useTranslation();

  const showLocation = (
    event: React.MouseEvent<HTMLTableRowElement>,
    id: string
  ) => {
    linkHandler(event, Routes.LOCATION(id), history);
  };

  const searchHandler = useCallback((operatorId?: string) => {
    dispatch(actions.listLocations(true, operatorId));
  }, [dispatch])

  useEffect(() => {
    if(!authUser?.isPrimary) searchHandler();
  }, [authUser, searchHandler]);

  return (
    <>
      {error && <Alert children={error} />}
      <Container>
        <h2>{t(ETranslation.LOCATIONS)}</h2>
        <Button
          onClick={() => history.push(Routes.LOCATION(ERouteStaticParams.new))}
          icon={faPlus}
          style={{ marginBottom: "1rem" }}
        >
          {t(ETranslation.LOCATIONS_NEW)}
        </Button>
        {authUser?.isPrimary && <LocationsSearch onSearch={searchHandler} />}
        {loading ? (
          <Spinner />
        ) : locations && sortedLocations ? (
          <Table hover sortContext={{ sort, onSort }}>
            <thead>
              <tr>
                { authUser?.isPrimary && <TableTh sort="operator.name">{t(ETranslation.COMMON_OPERATOR_NAME)}</TableTh> }
                <TableTh sort="name">{t(ETranslation.LOCATION_NAME)}</TableTh>
                <TableTh sort="locationToken">{t(ETranslation.COMMON_TOKEN)}</TableTh>
                <TableTh sort="nameGenitive">{t(ETranslation.LOCATION_NAME_GENITIVE)}</TableTh>
                <TableTh sort="nameInessive">{t(ETranslation.LOCATION_NAME_INESSIVE)}</TableTh>
                <TableTh sort="content">{t(ETranslation.LOCATION_CONTENT)}</TableTh>
                <TableTh sort="latitude">{t(ETranslation.LOCATION_LATITUDE)}</TableTh>
                <TableTh sort="longitude">{t(ETranslation.LOCATION_LONGITUDE)}</TableTh>
                <th>{t(ETranslation.SERVICES)}</th>
                <TableTh sort="zoomLevel" type="int">{t(ETranslation.LOCATION_ZOOM_LEVEL)}</TableTh>
              </tr>
            </thead>
            <tbody>
              {sortedLocations.map((location) => (
                <tr key={location.id} onClick={(e) => showLocation(e, location.id)}>
                  { authUser?.isPrimary && <td>{location.operator.name}</td>}
                  <td>{location.name}</td>
                  <td>{location.locationToken}</td>
                  <td>{location.nameGenitive}</td>
                  <td>{location.nameInessive}</td>
                  <td>{location.content}</td>
                  <td>{location.latitude}</td>
                  <td>{location.longitude}</td>
                  <td>{location.services?.map((s) => s.name).join(", ")}</td>
                  <td>{location.zoomLevel}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>{t(ETranslation.NO_LOCATIONS)}</p>
        )}
      </Container>
    </>
  );
};

export default Locations;
